import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Plus, Check, ChevronRight, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import AddressSearch from '@/components/AddressSearch';
import ReturnToLiveButton from '@/components/ReturnToLiveButton';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: '무통장입금',   desc: '입금 확인 후 결제완료로 전환됩니다.' },
  { value: 'pg',            label: '신용 · 체크카드', desc: '라이트페이 TEST · 판매자별로 카드 승인이 따로 진행됩니다.' },
  { value: 'wallet',        label: '충전금',        desc: '충전금 결제 준비 중 (곧 오픈)' },
];

const emptyAddressForm = {
  label: '',
  recipient_name: '',
  phone: '',
  postal_code: '',
  address1: '',
  address2: '',
  memo: '',
  is_default: false,
  road_address: '',
  jibun_address: '',
  base_address: '',
  detail_address: '',
  extra_address: '',
};

const AddressForm = ({ value, setValue, onSubmit, onCancel, saving }) => {
  const detailRef = useRef(null);
  const applySearchResult = (r) => setValue({
    ...value,
    postal_code: r.postal_code,
    road_address: r.road_address,
    jibun_address: r.jibun_address,
    base_address: r.base_address,
    extra_address: r.extra_address,
    // 기존 스키마 호환: address1 = 기본 주소(+extra), address2 = 상세 주소는 그대로 두고 focus로 이동.
    address1: (r.base_address + (r.extra_address ? ' ' + r.extra_address : '')).trim(),
  });
  return (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 border border-dashed border-black/15 dark:border-white/15 rounded-2xl" data-testid="checkout-address-form">
    <input placeholder="배송지 이름 (예: 우리집)" className="input-field" data-testid="addr-label"
      value={value.label} onChange={(e) => setValue({ ...value, label: e.target.value })} />
    <input placeholder="받는 사람" className="input-field" data-testid="addr-name"
      value={value.recipient_name} onChange={(e) => setValue({ ...value, recipient_name: e.target.value })} />
    <input placeholder="휴대전화" className="input-field" data-testid="addr-phone"
      value={value.phone} onChange={(e) => setValue({ ...value, phone: e.target.value })} />
    <div className="flex gap-2">
      <input placeholder="우편번호" className="input-field flex-1" data-testid="addr-postal" readOnly
        value={value.postal_code} />
      <AddressSearch onComplete={applySearchResult} detailInputRef={detailRef} testId="addr-search-btn" />
    </div>
    <input placeholder="기본 주소 (주소검색으로 자동 입력)" className="input-field sm:col-span-2" data-testid="addr-line1" readOnly
      value={value.address1} />
    <input ref={detailRef} placeholder="상세 주소 (직접 입력)" className="input-field sm:col-span-2" data-testid="addr-line2"
      value={value.address2} onChange={(e) => setValue({ ...value, address2: e.target.value, detail_address: e.target.value })} />
    <input placeholder="배송 메모 (선택)" className="input-field sm:col-span-2" data-testid="addr-memo"
      value={value.memo} onChange={(e) => setValue({ ...value, memo: e.target.value })} />
    <label className="flex items-center gap-2 text-xs sm:col-span-2">
      <input type="checkbox" checked={value.is_default} onChange={(e) => setValue({ ...value, is_default: e.target.checked })} data-testid="addr-default" />
      기본 배송지로 지정
    </label>
    <div className="flex gap-2 sm:col-span-2">
      <button type="button" onClick={onSubmit} disabled={saving}
        className="flex-1 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold disabled:opacity-40"
        data-testid="addr-save">
        {saving ? '저장 중…' : '저장'}
      </button>
      <button type="button" onClick={onCancel}
        className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold"
        data-testid="addr-cancel">취소</button>
    </div>
    <style>{`.input-field{padding:.55rem .75rem;border:1px solid rgba(0,0,0,.12);border-radius:.6rem;font-size:.85rem;background:transparent}
      .dark .input-field{border-color:rgba(255,255,255,.15)}`}</style>
  </div>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addrForm, setAddrForm] = useState(emptyAddressForm);
  const [savingAddress, setSavingAddress] = useState(false);
  const [cart, setCart] = useState(null);
  const [directPayload, setDirectPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [buyerMessage, setBuyerMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [depositorName, setDepositorName] = useState('');
  const [depositAccounts, setDepositAccounts] = useState([]);

  // LIGHTPAY-PG-PRODUCTION-READY-1 · 장바구니 선택 주문 (선택된 cart item 만 주문서·POST /orders cart_item_ids).
  const [cartSelection] = useState(() => {
    try { const v = JSON.parse(sessionStorage.getItem('livemoa:cart_selection') || 'null'); return Array.isArray(v) ? v : null; } catch { return null; }
  });

  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem('livemoa:pending_order'); } catch {}
    if (raw) {
      try { setDirectPayload(JSON.parse(raw)); } catch {}
    }
    (async () => {
      try {
        const [addrRes, cartRes] = await Promise.all([
          api.get('/buyer/addresses'),
          api.get('/cart'),
        ]);
        setAddresses(addrRes.data || []);
        setCart(cartRes.data || { items: [] });
        const def = (addrRes.data || []).find((a) => a.is_default);
        setSelectedAddressId(def?.address_id || (addrRes.data || [])[0]?.address_id || '');
      } catch (e) {
        if (e?.response?.status === 401) {
          toast.error('로그인이 필요합니다.');
          navigate('/login');
        }
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const saveAddress = async () => {
    if (savingAddress) return;
    if (!addrForm.recipient_name || !addrForm.phone || !addrForm.postal_code || !addrForm.address1) {
      toast.error('필수 항목을 입력해주세요.');
      return;
    }
    setSavingAddress(true);
    try {
      const res = await api.post('/buyer/addresses', addrForm);
      const list = await api.get('/buyer/addresses');
      setAddresses(list.data || []);
      setSelectedAddressId(res.data.address_id);
      setShowAddForm(false);
      setAddrForm(emptyAddressForm);
      toast.success('배송지가 저장되었습니다.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '배송지 저장에 실패했습니다.');
    } finally { setSavingAddress(false); }
  };

  // Compute summary lines. When direct-order payload exists, prefer it.
  const summary = useMemo(() => {
    if (directPayload) {
      const line = {
        product_id: directPayload.product_id,
        name: directPayload.name || directPayload.name_snapshot || '상품',
        image: directPayload.image || directPayload.image_snapshot,
        option: directPayload.option || {},
        quantity: directPayload.quantity || 1,
        unit_price: directPayload.unit_price || Math.round(directPayload.subtotal / (directPayload.quantity || 1)),
        line_total: directPayload.subtotal || (directPayload.unit_price || 0) * (directPayload.quantity || 1),
        broadcast_id: directPayload.broadcast_id,
      };
      return {
        items: [line],
        subtotal: line.line_total,
        shipping: Number(directPayload.shipping || 0),
        total: line.line_total + Number(directPayload.shipping || 0),
        count: 1,
      };
    }
    const sel = cartSelection;
    const items = (cart?.items || []).filter((it) => !sel || sel.includes(it.item_id)).map((it) => ({
      cart_item_id: it.item_id,
      product_id: it.product_id,
      name: it.name_snapshot,
      image: it.image_snapshot,
      option: it.option || {},
      quantity: it.quantity,
      unit_price: it.price_snapshot,
      line_total: (it.price_snapshot || 0) * (it.quantity || 0),
      broadcast_id: it.broadcast_id,
    }));
    const subtotal = items.reduce((s, i) => s + i.line_total, 0);
    return { items, subtotal, shipping: null, total: subtotal, count: items.length };
  }, [cart, directPayload, cartSelection]);

  const productIdsKey = (summary.items || []).map((i) => i.product_id).join(',');
  useEffect(() => {
    if (!productIdsKey) { setDepositAccounts([]); return; }
    api.get('/public/deposit-accounts', { params: { product_ids: productIdsKey } })
      .then((r) => setDepositAccounts(r.data || [])).catch(() => setDepositAccounts([]));
  }, [productIdsKey]);
  const bankUnavailable = depositAccounts.some((a) => !a.available);
  useEffect(() => {
    if (bankUnavailable && paymentMethod === 'bank_transfer') setPaymentMethod('pg');
  }, [bankUnavailable, paymentMethod]);

  const placeOrder = async () => {
    if (placing) return;
    if (!selectedAddressId) { toast.error('배송지를 선택해주세요.'); return; }
    if (paymentMethod === 'bank_transfer' && !depositorName.trim()) { toast.error('입금 예정자명을 입력해주세요.', { id: 'depositor-required' }); return; }
    toast.dismiss('depositor-required');
    if ((summary.items || []).length === 0) { toast.error('주문할 상품이 없습니다.'); return; }
    setPlacing(true);
    try {
      const body = {
        address_id: selectedAddressId,
        buyer_message: buyerMessage,
        payment_method: paymentMethod,
        ...(paymentMethod === 'bank_transfer' ? { depositor_name: depositorName.trim() } : {}),
      };
      if (directPayload) {
        body.items = summary.items.map((i) => ({
          product_id: i.product_id, quantity: i.quantity, option: i.option, broadcast_id: i.broadcast_id,
        }));
        // Prod-S4 · direct order (Buy Now) attribution top-level 전달
        if (directPayload.attribution) body.attribution = directPayload.attribution;
      } else {
        body.cart_item_ids = summary.items.map((i) => i.cart_item_id);
      }
      const res = await api.post('/orders', body);
      try { sessionStorage.removeItem('livemoa:pending_order'); sessionStorage.removeItem('livemoa:cart_selection'); } catch {}
      // STEP 8-I-4A · Toast 없음 · 성공 알림은 오직 댓글 🎉 Activity 로만.
      navigate(paymentMethod === 'pg' ? `/orders/${res.data.order_id}/pay` : `/orders/${res.data.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '주문 생성에 실패했습니다.');
    } finally { setPlacing(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-32 sm:pb-8" data-testid="checkout-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <div className="flex items-center justify-between mb-4">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white">
            <ArrowLeft size={14} /> 뒤로
          </button>
          {/* STEP 8-I-4 · Return-to-LIVE CTA */}
          <ReturnToLiveButton />
        </div>
        <div className="flex items-center gap-2 mb-6">
          <ShoppingBag className="text-[#FF3B30]" size={22} />
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>주문서</h1>
        </div>

        {loading ? (
          <div className="text-sm text-gray-400 py-6">불러오는 중…</div>
        ) : (
          <div className="space-y-8">
            {/* ---- Shipping addresses ---- */}
            <section data-testid="checkout-address-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-black flex items-center gap-2"><MapPin size={16} className="text-[#FF3B30]" /> 배송지</h2>
                <button type="button" onClick={() => { setShowAddForm((v) => !v); setAddrForm(emptyAddressForm); }}
                  className="text-xs font-bold text-[#FF3B30] inline-flex items-center gap-1"
                  data-testid="checkout-add-address">
                  <Plus size={12} /> 새 배송지
                </button>
              </div>
              {addresses.length === 0 && !showAddForm && (
                <p className="text-sm text-gray-500 mb-3" data-testid="checkout-no-address">등록된 배송지가 없습니다. 새 배송지를 추가해주세요.</p>
              )}
              <div className="space-y-2">
                {addresses.map((a) => (
                  <label key={a.address_id} className={`block cursor-pointer rounded-xl border p-3 transition-colors ${selectedAddressId === a.address_id ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`}
                    data-testid={`checkout-address-${a.address_id}`}>
                    <div className="flex items-start gap-2">
                      <input type="radio" name="addr" checked={selectedAddressId === a.address_id} onChange={() => setSelectedAddressId(a.address_id)} className="mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-sm">{a.label || '배송지'}</p>
                          {a.is_default && <span className="text-[10px] font-black text-[#FF3B30] uppercase tracking-widest">기본</span>}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{a.recipient_name} · {a.phone}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">[{a.postal_code}] {a.address1} {a.address2}</p>
                        {a.memo && <p className="text-[11px] text-gray-500 mt-1">메모: {a.memo}</p>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {showAddForm && (
                <div className="mt-3">
                  <AddressForm
                    value={addrForm}
                    setValue={setAddrForm}
                    onSubmit={saveAddress}
                    onCancel={() => { setShowAddForm(false); setAddrForm(emptyAddressForm); }}
                    saving={savingAddress}
                  />
                </div>
              )}
            </section>

            {/* ---- Items summary ---- */}
            <section data-testid="checkout-items-section">
              <h2 className="text-base font-black mb-3">주문 상품 ({summary.items.length}건)</h2>
              <div className="space-y-2">
                {summary.items.map((it, idx) => (
                  <div key={`${it.product_id}-${idx}`} className="flex items-center gap-3 p-3 rounded-xl border border-black/10 dark:border-white/10">
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0">
                      <SafeImg src={it.image} kind="product" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{it.name}</p>
                      {Object.keys(it.option || {}).length > 0 && (
                        <p className="text-[11px] text-gray-500 truncate">{Object.entries(it.option).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
                      )}
                      <p className="text-xs mt-0.5">{won(it.unit_price)} × {it.quantity}</p>
                    </div>
                    <p className="font-black text-sm">{won(it.line_total)}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ---- Buyer message ---- */}
            <section data-testid="checkout-message-section">
              <h2 className="text-base font-black mb-2">배송 요청사항</h2>
              <input type="text" value={buyerMessage} onChange={(e) => setBuyerMessage(e.target.value)}
                placeholder="예: 부재 시 경비실에 맡겨주세요"
                className="w-full input-field" data-testid="checkout-buyer-message" />
            </section>

            {/* ---- Payment method ---- */}
            <section data-testid="checkout-payment-section">
              <h2 className="text-base font-black mb-3">결제 방법</h2>
              <div className="space-y-2">
                {PAYMENT_METHODS.map((m) => (
                  <label key={m.value} className={`block rounded-xl border p-3 transition-colors ${m.value === 'bank_transfer' && bankUnavailable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${paymentMethod === m.value ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`}
                    data-testid={`checkout-payment-${m.value}`}>
                    <div className="flex items-start gap-2">
                      <input type="radio" name="pay" checked={paymentMethod === m.value} disabled={m.value === 'bank_transfer' && bankUnavailable} onChange={() => setPaymentMethod(m.value)} className="mt-1" />
                      <div className="flex-1">
                        <p className="font-bold text-sm">{m.label}</p>
                        <p className="text-[11px] text-gray-500">{m.desc}</p>
                        {m.value === 'bank_transfer' && bankUnavailable && (
                          <p className="text-[11px] font-bold text-[#FF3B30]" data-testid="checkout-bank-unavailable">판매자의 입금계좌가 등록되지 않아 무통장입금을 이용할 수 없습니다.</p>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {paymentMethod === 'pg' ? (
                <p className="mt-3 text-[11px] text-gray-500" data-testid="checkout-pg-note">주문 접수 후 판매자별 결제 화면으로 이동합니다. 판매자마다 카드 승인이 따로 진행됩니다.</p>
              ) : paymentMethod === 'bank_transfer' ? (
                <div className="mt-3 space-y-2" data-testid="checkout-bank-section">
                  <label htmlFor="checkout-depositor-name" className="block text-xs font-bold">입금 예정자명</label>
                  <input id="checkout-depositor-name" type="text" value={depositorName} maxLength={20} onChange={(e) => setDepositorName(e.target.value)}
                    placeholder="입금 예정자명 (주문자명과 달라도 됩니다)" className="w-full px-3 py-2 border rounded-xl bg-white dark:bg-[#0A0A0A] border-black/15 dark:border-white/15 text-sm" data-testid="checkout-depositor-name" />
                  <p className="text-[11px] text-amber-700" data-testid="checkout-deposit-delay-notice">무통장입금 후 결제완료 처리까지 시간이 걸릴 수 있습니다. 입금자명과 입금금액을 정확히 확인해 주세요.</p>
                  {depositAccounts.map((a) => (
                    <p key={a.seller_id} className="text-[11px] text-gray-600 dark:text-gray-400" data-testid={`checkout-deposit-account-${a.seller_id}`}>
                      <strong>{a.seller_name}</strong> · {a.available ? `${a.bank} ${a.account_number_masked} (${a.account_holder})` : '입금 계좌 미등록'}
                    </p>
                  ))}
                  <p className="text-[11px] text-gray-500">주문 후 판매자별 전체 계좌번호와 입금액이 주문 상세에 안내됩니다. 여러 판매자 상품은 판매자마다 따로 입금해주세요.</p>
                </div>
              ) : <p className="mt-3 text-[11px] text-gray-500">현재 단계에서는 실제 결제가 수행되지 않으며 <strong className="text-[#FF3B30]">결제대기</strong> 상태로 주문이 접수됩니다. 관리자가 결제 확인 시 <strong>결제완료</strong>로 전환됩니다.</p>}
            </section>

            {/* ---- Totals ---- */}
            <section data-testid="checkout-total-section" className="rounded-2xl border border-black/10 dark:border-white/10 p-4 space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">상품 금액</span>
                <span data-testid="checkout-subtotal">{won(summary.subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">배송비</span>
                <span data-testid="checkout-shipping">
                  {summary.shipping == null ? '판매자별 정책에 따라 계산됨' : won(summary.shipping)}
                </span>
              </div>
              <div className="pt-2 mt-2 border-t border-black/5 dark:border-white/10 flex items-center justify-between">
                <span className="text-sm font-bold">예상 결제 금액</span>
                <span className="text-2xl font-black text-[#FF3B30]" data-testid="checkout-total">{won(summary.total)}</span>
              </div>
            </section>
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 bg-white dark:bg-[#0A0A0A] border-t border-black/10 dark:border-white/10 px-4 py-3 sm:static sm:border-none sm:mt-6 sm:max-w-3xl sm:mx-auto sm:px-6">
        <button type="button" onClick={placeOrder}
          disabled={placing || loading || (summary.items || []).length === 0 || !selectedAddressId}
          className="w-full py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold disabled:opacity-40"
          data-testid="checkout-submit">
          {placing ? '주문 접수 중…' : (paymentMethod === 'pg' ? '주문하고 카드결제하기' : '결제대기로 주문 접수')}
        </button>
      </div>
    </div>
  );
};

export default Checkout;
